// components/entities/CreditGrantRenderer.tsx
import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import { Building, FileText, Loader2, Upload } from '@/components/ui/lucide-memo';
import { formatCreditsInteger } from '#lib/formatCredits';
import { invoiceTotals } from '#lib/invoiceTotals';
import type { BaseEntityProps, CreditGrantDTO } from '#types/types';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';
import { SELECT_CHEVRON_CLASS, SELECT_CHEVRON_STYLE } from '../portal/tableSurface';
import { EntitySkeleton } from './EntityView';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'PP');
  } catch {
    return '-';
  }
};

const money = (amount: number | null, currency: string | null): string => {
  if (amount === null) return '-';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'EUR', maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${currency ?? ''}`.trim();
  }
};

export interface CreditGrantRendererProps extends BaseEntityProps<CreditGrantDTO> {
  isExpanded?: boolean;
  columns?: TableColumnDef[];
  /** Invoice options for this row's organisation, so the invoice cell can be assigned in place. */
  invoiceOptions?: Array<{ id: string; label: string }>;
}

const DEFAULT_COLUMNS: TableColumnDef[] = [
  { id: 'organisation', header: '', minWidth: 96 },
  { id: 'grant', header: '', minWidth: 260 },
  { id: 'credits', header: '', minWidth: 110 },
  { id: 'used', header: '', minWidth: 110 },
  { id: 'remaining', header: '', minWidth: 110 },
  { id: 'activated', header: '', minWidth: 120 },
  { id: 'invoice', header: '', minWidth: 170 },
  { id: 'invoiceTotal', header: '', minWidth: 120 },
  { id: 'status', header: '', minWidth: 90 },
];

const CELL_STYLE_START: React.CSSProperties = { justifyContent: 'flex-start' };
const CELL_STYLE_CENTER: React.CSSProperties = { justifyContent: 'center' };
const CELL_STYLE_END: React.CSSProperties = { justifyContent: 'flex-end' };

const resolveCellStyle = (column: TableColumnDef): React.CSSProperties => {
  if (column.align === 'right') return CELL_STYLE_END;
  if (column.align === 'center') return CELL_STYLE_CENTER;
  return CELL_STYLE_START;
};

export const CreditGrantRenderer = React.memo(({
  entityId,
  variant = 'row',
  isExpanded,
  onClick,
  columns,
  className,
  invoiceOptions,
}: CreditGrantRendererProps) => {
  const data = useKernelStore((state) => state.inventory.CreditGrant.registry[entityId]) as CreditGrantDTO | undefined;
  const { kernel } = useKernel();
  const [uploadingInvoiceId, setUploadingInvoiceId] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const [editing, setEditing] = useState<'name' | 'credits' | null>(null);
  const [editValue, setEditValue] = useState('');

  // A cell writes only itself, on blur or Enter, and only when the value actually changed.
  // There is no save button: the row is the editor, the way a database row behaves.
  const commit = useCallback(async (field: 'name' | 'credits' | 'invoice', raw: string) => {
    if (!data) return;
    // A service row is an invoice: its name is the invoice's category label.
    const payload: Record<string, unknown> = data.kind === 'service'
      ? { action: 'updateInvoice', organisationId: data.organisationId, invoiceId: data.id }
      : { action: 'updateCreditGrant', organisationId: data.organisationId, planId: data.id };
    if (field === 'name') {
      // Blank means "never mind", not "erase": a cleared cell wiped a grant's name on 2026-09-26.
      if (!raw.trim() || raw.trim() === (data.name ?? '')) { setEditing(null); return; }
      payload[data.kind === 'service' ? 'category' : 'planName'] = raw.trim();
    }
    if (field === 'credits') {
      const credits = Number.parseInt(raw.replace(/[^0-9]/g, ''), 10);
      if (!Number.isInteger(credits) || credits < 0) { toast.error('Credits must be a whole number.'); return; }
      if (credits === data.includedCredits) { setEditing(null); return; }
      payload.includedCredits = credits;
    }
    if (field === 'invoice') {
      if (!raw || data.invoices.some((invoice) => invoice.id === raw)) return;
      payload.invoiceId = raw;
    }
    setEditing(null);
    try {
      await axios.post('/api/admin', payload);
      // The saved value is painted at once, so the cell never shows the old text while the list
      // re-reads: reopening a stale cell and clicking away is what saved an empty name.
      const patch: Partial<CreditGrantDTO> = {
        ...(typeof (payload.planName ?? payload.category) === 'string' ? { name: String(payload.planName ?? payload.category) } : {}),
        ...(typeof payload.includedCredits === 'number' ? { includedCredits: payload.includedCredits } : {}),
      };
      kernel.current?.setInventorySlice('CreditGrant', (prev) => {
        const current = prev.registry[data.id];
        return {
        ...prev,
        registry: current ? { ...prev.registry, [data.id]: { ...current, ...patch } } : prev.registry,
        queryCache: {}, lastActivity: Date.now(), lastCreateActivity: Date.now(),
        };
      });
    } catch (error: unknown) {
      toast.error(axios.isAxiosError(error) ? String(error.response?.data ?? error.message) : 'Could not save the change.');
    }
  }, [data, kernel]);

  const handleUpload = useCallback(async (invoiceId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !data) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF invoices are accepted.');
      return;
    }
    setUploadingInvoiceId(invoiceId);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Could not read the file.'));
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.readAsDataURL(file);
      });
      await axios.post('/api/admin', {
        action: 'uploadInvoiceDocument',
        organisationId: data.organisationId,
        invoiceId,
        documentBase64: base64,
        documentMimeType: file.type,
      });
      toast.success('Invoice document attached.');
      kernel.current?.setInventorySlice('CreditGrant', (prev) => ({
        ...prev, queryCache: {}, lastActivity: Date.now(), lastCreateActivity: Date.now(),
      }));
    } catch (error: unknown) {
      toast.error(axios.isAxiosError(error) ? String(error.response?.data ?? error.message) : 'Upload failed.');
    } finally {
      setUploadingInvoiceId(null);
    }
  }, [data, kernel]);

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  if (variant === 'chip') {
    return (
      <div className={clsx('flex items-center gap-2 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/80', className)}>
        <FileText size={12} />
        <span className="truncate">{data.name || 'Unnamed grant'}</span>
      </div>
    );
  }

  const remaining = Math.max(data.includedCredits - data.usedCredits, 0);
  // A service row is an invoice that funds no credits: nothing on it is a grant to edit.
  const isService = data.kind === 'service';
  const totals = invoiceTotals(data.invoices);
  const linkable = (invoiceOptions ?? []).filter((option) => !data.invoices.some((invoice) => invoice.id === option.id));

  const cellRenderers: Record<string, React.ReactNode> = {
    // Same logo source and fallback as the Organisations table; the name stays on hover.
    organisation: (
      <div className="flex h-8 w-8 items-center justify-center overflow-hidden" title={data.organisationName}>
        {logoFailed ? (
          <Building size={16} className="text-white/25" />
        ) : (
          <img
            src={`/api/organisation/logo?orgId=${data.organisationId}`}
            alt={data.organisationName}
            className="h-full w-full object-contain"
            onError={() => setLogoFailed(true)}
          />
        )}
      </div>
    ),
    grant: editing === 'name' ? (
      <input
        autoFocus
        className="h-7 w-full rounded border border-white/25 bg-white/[0.04] px-1 text-sm text-white/90 outline-none"
        value={editValue}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setEditValue(event.target.value)}
        onBlur={() => void commit('name', editValue)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); void commit('name', editValue); }
          if (event.key === 'Escape') setEditing(null);
        }}
      />
    ) : (
      <div
        className="flex h-full w-full min-w-0 flex-col justify-center"
        data-cell-editable=""
        onClick={(event) => { event.stopPropagation(); setEditValue(data.name ?? ''); setEditing('name'); }}
      >
        <span className="truncate text-sm text-white/90">{data.name || 'Unnamed grant'}</span>
      </div>
    ),
    credits: editing === 'credits' ? (
      <input
        autoFocus
        className="h-7 w-full rounded border border-white/25 bg-white/[0.04] px-1 font-mono text-xs text-white/90 outline-none"
        value={editValue}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setEditValue(event.target.value)}
        onBlur={() => void commit('credits', editValue)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); void commit('credits', editValue); }
          if (event.key === 'Escape') setEditing(null);
        }}
      />
    ) : isService ? <span className="text-xs text-white/30">-</span> : (
      <span
        className="flex h-full w-full items-center font-mono text-xs text-white/85"
        data-cell-editable=""
        onClick={(event) => { event.stopPropagation(); setEditValue(String(data.includedCredits)); setEditing('credits'); }}
      >
        {formatCreditsInteger(data.includedCredits)}
      </span>
    ),
    used: isService ? <span className="text-xs text-white/30">-</span> : <span className="font-mono text-xs text-white/55">{formatCreditsInteger(data.usedCredits)}</span>,
    remaining: isService ? <span className="text-xs text-white/30">-</span> : <span className="font-mono text-xs text-white/70">{formatCreditsInteger(remaining)}</span>,
    activated: <span className="text-sm text-white/60">{formatDate(data.activationDate)}</span>,
    // The invoice cell always offers the next step: open each PDF, attach a missing one, or link
    // an invoice. A grant can carry several (a deposit and its balance), so they stack.
    invoice: (
      <div className="flex min-w-0 flex-col gap-0.5" onClick={(event) => event.stopPropagation()}>
        {data.invoices.map((invoice) => invoice.documentUrl ? (
          <a
            key={invoice.id}
            href={`/api/inventory/invoices/${invoice.id}/document`}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => {
              // Cmd/Ctrl/middle-click keep the browser's own "open in a new tab".
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              // Inside Settings (Admin > Finance) the preview would replace Settings itself: new tab.
              if (kernel.current?.getState().activeModal === 'UserSettings') return;
              event.preventDefault();
              kernel.current?.openModal('DocumentPreview', { url: event.currentTarget.href, title: invoice.number });
            }}
            className="inline-flex min-w-0 items-center gap-1.5 text-xs text-[#ECECEC] hover:underline"
          >
            <FileText size={12} className="shrink-0 text-[#8E8E8E]" />
            <span className="truncate">{invoice.number}</span>
          </a>
        ) : (
          <label key={invoice.id} className="inline-flex min-w-0 cursor-pointer items-center gap-1.5 text-xs text-white/55 hover:text-white/85" title="Attach the invoice PDF">
            {uploadingInvoiceId === invoice.id ? <Loader2 size={12} className="shrink-0 animate-spin" /> : <Upload size={12} className="shrink-0" />}
            <span className="truncate">{invoice.number}</span>
            <input type="file" accept="application/pdf" className="hidden" disabled={uploadingInvoiceId !== null} onChange={(event) => void handleUpload(invoice.id, event)} />
          </label>
        ))}
        {!isService && data.invoices.length === 0 ? (
          <select
            className={`h-6 w-full rounded border border-transparent bg-transparent text-xs text-[#8E8E8E] outline-none hover:border-white/10 ${SELECT_CHEVRON_CLASS}`}
            style={SELECT_CHEVRON_STYLE}
            value=""
            onChange={(event) => void commit('invoice', event.target.value)}
            aria-label="Link an invoice to this grant"
          >
            <option value="">No invoice</option>
            {linkable.map((invoice) => <option key={invoice.id} value={invoice.id}>{invoice.label}</option>)}
          </select>
        ) : null}
      </div>
    ),
    invoiceTotal: <span className="font-mono text-xs text-white/55">{totals.length ? totals.map(([currency, amount]) => money(amount, currency)).join(' + ') : '-'}</span>,
    status: <span className={clsx('text-xs', data.isActive ? 'text-white/50' : 'text-white/30')}>{data.isActive ? 'Active' : 'Inactive'}</span>,
  };

  const activeColumns = columns || DEFAULT_COLUMNS;

  return (
    <div
      className={clsx(
        styles.gridRow,
        styles.bodyRow,
        'group w-full border-b border-white/5 hover:bg-white/[0.02]',
        isExpanded && 'bg-white/[0.02] border-white/10',
        className,
      )}
      onClick={onClick}
    >
      {activeColumns.map((column) => (
        <div key={column.id} className={styles.cell} style={resolveCellStyle(column)}>
          {cellRenderers[column.id] || null}
        </div>
      ))}
    </div>
  );
});

CreditGrantRenderer.displayName = 'CreditGrantRenderer';
